import React, { useEffect, useRef, useState } from "react";
import { motion, useInView } from "framer-motion";
import { SECTION } from "@/constants/testIds";
import { useI18n } from "@/i18n/I18nContext";

function Counter({ value, duration = 1400 }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const [n, setN] = useState(0);

  useEffect(() => {
    if (!inView) return;
    const start = performance.now();
    let raf;
    const tick = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(value * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [inView, value, duration]);

  return <span ref={ref}>{n}</span>;
}

export default function KeyFigures() {
  const { t } = useI18n();
  const f = t.figures;

  return (
    <section
      data-testid={SECTION.figures}
      className="relative section-y bg-navy text-white overflow-hidden"
    >
      <div className="absolute inset-0 -z-0">
        <div className="absolute inset-0 grid-bg opacity-30" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[420px] w-[820px] bg-cyan-tech/10 blur-3xl rounded-full" />
      </div>
      <div className="container-x relative">
        <div className="max-w-3xl">
          <span className="text-[11px] uppercase tracking-[0.22em] text-cyan-tech font-semibold">
            {f.overline}
          </span>
          <h2 className="mt-4 font-display font-semibold text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-white">
            {f.title}
          </h2>
        </div>

        <div className="mt-14 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 rounded-3xl overflow-hidden border border-white/10">
          {f.list.map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.45, delay: i * 0.05 }}
              data-testid={`figure-${i}`}
              className="bg-navy p-8 hover:bg-[#0F2240] transition-colors"
            >
              <div className="font-display text-4xl lg:text-5xl font-semibold tracking-tight">
                {item.prefix || ""}
                <Counter value={item.value} />
                {item.suffix || ""}
              </div>
              <div className="mt-3 text-sm text-slate-300">{item.label}</div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
